import React, { FC, ReactNode, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  NativeSyntheticEvent,
  TextInput as RNTextInput,
  TextInputEndEditingEventData,
  TextInputFocusEventData,
  TextInputProps
} from 'react-native';
import styled from 'styled-components/native';

import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { isRTL } from '@webtrader/core/i18n';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { IValidationRegex } from '@webtrader/core/utils';
import { containsOnlyNumbers } from '@webtrader/core/validators';
import { checkIsNative } from '@webtrader/core/validators/device';

import FeatherIcons from '@/components/Icons/Feather';
import Ionicons from '@/components/Icons/Ionic';
import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';
import { checkIsAndroid } from '@/utils/device';

import { DEFAULT_BORDERED_INPUT_STYLES } from '../Settings';
import { BaseTextInputSize, getInputsizeSettings } from './Configs/Size';

export interface IBaseTextInputProps extends Omit<TextInputProps, 'onBlur' | 'onEndEditing' | 'selectionState'> {
  size?: BaseTextInputSize;
  label?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  clearable?: boolean;
  borderless?: boolean;
  onlyNumeric?: boolean;
  validRegex?: RegExp | IValidationRegex;
  testID?: EDataTestId;
  onBlur?: (e: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  onEndEditing?: (e: NativeSyntheticEvent<TextInputFocusEventData | TextInputEndEditingEventData>) => void;
}

interface IBaseInputRowProps {
  focused?: boolean;
  borderless?: boolean;
}

const TextInput: FC<IBaseTextInputProps> = (props) => {
  const {
    size = 'default',
    label,
    value,
    prefix,
    suffix,
    clearable,
    multiline = false,
    spellCheck = false,
    borderless,
    autoCorrect = false,
    numberOfLines = 1,
    autoCapitalize = 'none',
    secureTextEntry,
    onlyNumeric = false,
    validRegex,
    testID,
    onChangeText,
    onBlur,
    onFocus,
    ...restProps
  } = props;

  const { theme } = useTheme();

  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!onlyNumeric) {
      setInputValue(value ?? '');
    } else {
      if (onlyNumeric && (!value || containsOnlyNumbers(value))) {
        setInputValue(value ?? '');
      }
    }
  }, [onlyNumeric, value]);

  const onBlurEvent = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    onBlur?.(e);
    setFocused(false);
  };

  const handleFocus = (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
    onFocus?.(e);
    setFocused(true);
  };

  const handleChangeText = useCallback(
    (nextValue: string) => {
      if (validRegex && nextValue && !validRegex.test(nextValue)) {
        return;
      }

      if (!onlyNumeric) {
        setInputValue(nextValue);
        onChangeText?.(nextValue);
      } else {
        if (onlyNumeric && (!nextValue || containsOnlyNumbers(nextValue))) {
          setInputValue(nextValue);
          onChangeText?.(nextValue);
        }
      }
    },
    [validRegex, onlyNumeric, onChangeText]
  );

  const isEmptyText = useMemo(() => {
    return !inputValue || inputValue === '';
  }, [inputValue]);

  const passwordVisibilityToggle = useMemo(() => {
    if (clearable && !isEmptyText) {
      const clearText = () => handleChangeText('');

      return (
        <InputClearTextButton onPress={clearText}>
          <FeatherIcons name="x-circle" size={16} color={theme.colors.text[2]} />
        </InputClearTextButton>
      );
    }

    if (!secureTextEntry) {
      return null;
    }

    const onToggle = () => {
      setShowPassword(!showPassword);
    };

    return (
      <InputPasswordVisibilityToggle onPress={onToggle} activeOpacity={1}>
        <Ionicons
          name={showPassword ? 'eye' : 'eye-off'}
          color={theme.colors.text[2]}
          testID={showPassword ? EDataTestId.INPUT_PASSSWORD_VISIBLE : EDataTestId.INPUT_PASSSWORD_HIDDEN}
        />
      </InputPasswordVisibilityToggle>
    );
  }, [theme, clearable, showPassword, isEmptyText, secureTextEntry, handleChangeText]);

  const { height, fontSize } = useMemo(() => {
    return getInputsizeSettings(size);
  }, [size]);

  return (
    <InputField>
      {label && <InputFieldLabel>{label}</InputFieldLabel>}
      <InputRow focused={focused} borderless={borderless}>
        {prefix && <InputRowPrefix>{prefix}</InputRowPrefix>}
        <Input
          {...restProps}
          onBlur={(e: NativeSyntheticEvent<TextInputFocusEventData>) => {
            if (!checkIsNative()) {
              onBlurEvent(e);
            }
          }}
          onEndEditing={(e: NativeSyntheticEvent<TextInputFocusEventData | TextInputEndEditingEventData>) => {
            if (checkIsNative()) {
              onBlurEvent(e as NativeSyntheticEvent<TextInputFocusEventData>);
            }
          }}
          onFocus={handleFocus}
          onChangeText={handleChangeText}
          value={inputValue}
          height={height}
          fontSize={fontSize}
          multiline={multiline}
          spellCheck={spellCheck}
          autoCorrect={autoCorrect}
          numberOfLines={numberOfLines}
          autoCapitalize={autoCapitalize}
          secureTextEntry={secureTextEntry && !showPassword}
          selectionColor={theme.colors.primary}
          placeholderTextColor={theme.colors.text[2]}
          testID={testID}
        />
        {passwordVisibilityToggle}
        {suffix && <InputRowSuffix>{suffix}</InputRowSuffix>}
      </InputRow>
    </InputField>
  );
};

export default memo(TextInput);

const InputField = styled.View`
  display: flex;
`;

const InputFieldLabel = styled(Text)`
  align-self: flex-start;

  font-size: ${DEFAULT_BORDERED_INPUT_STYLES.label.fontSize}px;
  font-weight: ${DEFAULT_BORDERED_INPUT_STYLES.label.fontWeight};

  padding-bottom: ${DEFAULT_BORDERED_INPUT_STYLES.label.paddingBottom}px;
`;

const InputRow = styled.View<IBaseInputRowProps>`
  align-items: center;
  flex-direction: row;

  border-width: ${({ borderless }) => (borderless ? 0 : 1)}px;
  border-color: ${({ theme, focused }) => (focused ? theme.colors.primary : theme.colors.border[1])};
  border-radius: ${DEFAULT_BORDERED_INPUT_STYLES.borderRadius}px;
`;

const InputRowPrefix = styled.View`
  padding-start: ${DEFAULT_BORDERED_INPUT_STYLES.paddingEnd}px;
`;

const InputRowSuffix = styled.View`
  padding-end: ${DEFAULT_BORDERED_INPUT_STYLES.paddingEnd}px;
`;

const InputClearTextButton = styled(TouchableOpacity)`
  align-items: center;
  justify-content: center;

  padding-top: ${dynamicSize.convertSize(2)}px;
  padding-end: ${DEFAULT_BORDERED_INPUT_STYLES.paddingEnd}px;
`;

const InputPasswordVisibilityToggle = styled(TouchableOpacity)`
  padding-end: ${DEFAULT_BORDERED_INPUT_STYLES.paddingEnd}px;
`;

//  Fix for Android: TextInput's content is not center vertically (@see: https://github.com/facebook/react-native/issues/7823)
const Input = styled(RNTextInput)<{ height?: number; fontSize?: number }>`
  flex: 1;

  overflow: hidden;

  height: ${({ height }) => dynamicSize.convertSize(height)}px;
  width: 100%;

  padding-top: 0;
  padding-bottom: 0;

  text-align: ${() => (isRTL() ? 'right' : 'left')};

  padding-start: ${DEFAULT_BORDERED_INPUT_STYLES.paddingStart}px;
  padding-end: ${DEFAULT_BORDERED_INPUT_STYLES.paddingEnd}px;

  color: ${({ theme }) => theme.colors.text[0]};
  font-size: ${({ fontSize }) => dynamicSize.convertSize(fontSize)}px;
  font-family: ${({ theme }) =>
    checkIsAndroid() ? theme.font.DEFAULT_ANDROID_FONT_FAMILY : theme.font.DEFAULT_FONT_FAMILY};
`;
